/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。 renderRowActions も同様。
 */
import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router'

import {
  HStack,
  Link,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { GcExcludedKomaMark } from '~/components/common/GcExcludedKomaMark'
import type { MasterDataTableColumn } from '~/components/common/MasterDataTable'
import { MasterDataTable } from '~/components/common/MasterDataTable'
import { ROW_ACTION_GAP } from '~/components/common/MasterDataTable/stickyActionColumn'
import { PlanConsistencyStatusBadge } from '~/components/common/PlanConsistencyStatusBadge'
import { PlanListColumnHeader } from '~/components/common/PlanListColumnHeader'
import {
  PlanStatusNote,
  RECEIPT_CHECK_SUSPENDED_NOTE,
  SEND_RETRYING_NOTE,
} from '~/components/common/PlanStatusNote'
import { PlanSubmissionStatusBadge } from '~/components/common/PlanSubmissionStatusBadge'
import { sortableColumn } from '~/components/common/SortableColumnHeader'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'
import { downloadBlockReason } from '~/models/planSubmission/downloadBlockReason'
import { canDownloadXml } from '~/models/planSubmission/xmlDownload'
import type { SortOrder } from '~/models/tableSort'
import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import { DownloadFormatMenuButton } from '~/pages/ProcurementPlanList/components/DownloadFormatMenuButton'
import { SpotTradeResultMismatchIcon } from '~/pages/ProcurementPlanList/components/SpotTradeResultMismatchIcon'
import { STATUS_BADGE_LABEL_KEYS } from '~/pages/ProcurementPlanList/constants'
import { useDownloadProcurementPlanXml } from '~/pages/ProcurementPlanList/hooks/useDownloadProcurementPlanXml'
import { formatTimestamp } from '~/pages/ProcurementPlanList/models/formatTimestamp'
import {
  type ProcurementPlanSort,
  type ProcurementPlanSortKey,
  nextProcurementPlanSort,
  sortProcurementPlanRows,
} from '~/pages/ProcurementPlanList/models/sortProcurementPlanRows'
import type { ProcurementPlanRowWithBadge } from '~/pages/ProcurementPlanList/models/summary'
import { routePaths } from '~/routes/routePaths'

import { AwaitingMatchResultMark } from './AwaitingMatchResultMark'

type Props = {
  rows: ProcurementPlanRowWithBadge[]
  isLoading: boolean
  /** workflow-back-bar-readonly AC-11: 詳細リンクへ引き継ぐ caseId (undefined = 引き継がない)。 */
  caseId?: string
}

export const ProcurementPlanTable = ({
  rows,
  isLoading,
  caseId,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { download } = useDownloadProcurementPlanXml()
  // AC-PPL-012-1: 列見出しを押すまではデフォルト順 (backend の返却順) のまま並べる。
  const [sort, setSort] = useState<ProcurementPlanSort | undefined>(undefined)

  const orderOf = (key: ProcurementPlanSortKey): SortOrder | undefined =>
    sort?.key === key ? sort.order : undefined
  const sortableColumnOf = (
    key: ProcurementPlanSortKey,
    label: string,
  ): ReturnType<typeof sortableColumn> =>
    sortableColumn({
      order: orderOf(key),
      label,
      nextOrder: nextProcurementPlanSort(sort, key).order,
      onClick: () => {
        setSort((prev) => nextProcurementPlanSort(prev, key))
      },
    })

  /**
   * 業務ワークフロー詳細 (= WorkflowCase 詳細) への遷移先 URL。
   * /workflow/cases/:caseId
   * procurement-plan-list-workflow-id-link design Decision 9:
   * DemandForecastList/components/ForecastTable.tsx の getWorkflowCasePath と
   * 同型 (共通 util への切り出しは Deferred)。
   */
  const getWorkflowCasePath = (workflowCaseId: string): string =>
    routePaths.workflowCaseDetail.path.replace(':caseId', workflowCaseId)

  const columns: Array<MasterDataTableColumn<ProcurementPlanRowWithBadge>> = [
    // ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // 旧実装は 6 番目 (提出日時 の後ろ) だったが、 design (`design/plan/procurement.html`) は
    // 既に先頭 (一括選択チェックボックス列を除くデータ列の 1 列目) に置いており、
    // 本変更で design 準拠に揃う。
    {
      key: 'status',
      header: (
        <PlanListColumnHeader
          help={t('page:procurementPlanList.table.statusHelp')}
          label={t('page:procurementPlanList.table.status')}
        />
      ),
      align: 'center',
      cell: ({ row, badge }) => (
        <>
          {/* design procurement.html: 食い違い (AC-PPP-020) と未着 (AC-PPP-019) は 1 つのアイコンで、食い違いを優先する */}
          {row.spotTradeResultMismatch ? (
            <SpotTradeResultMismatchIcon />
          ) : (
            <AwaitingMatchResultMark
              pendingBidCount={row.bidsAwaitingMatchResultCount}
            />
          )}
          <PlanSubmissionStatusBadge
            errorDetail={{
              errorCategory: row.errorCategory,
              receptionErrors: row.receptionErrors,
              breakdownRowLimit: row.breakdownRowLimit,
            }}
            hasWarning={badge.hasWarning}
            labelKeys={STATUS_BADGE_LABEL_KEYS}
            muted={badge.muted}
            variant={badge.variant}
          />
          {/* design procurement.html: 食い違いのアイコンを出す行には受付照合停止の注記を出さない */}
          <PlanStatusNote
            {...RECEIPT_CHECK_SUSPENDED_NOTE}
            visible={
              row.receiptCheckSuspendedByCallLimit &&
              !row.spotTradeResultMismatch
            }
          />
          <PlanStatusNote {...SEND_RETRYING_NOTE} visible={row.sendRetrying} />
          <GcExcludedKomaMark excludedKomaNumbers={row.excludedKomaNumbers} />
        </>
      ),
    },
    // 整合ステータスは提出ステータスの右 (AC-PPL-007)。並べ替え・絞り込みは spec に無いので付けない。
    {
      key: 'consistencyStatus',
      header: (
        <PlanListColumnHeader
          help={t('page:procurementPlanList.table.consistencyStatusHelp')}
          label={t('page:procurementPlanList.table.consistencyStatus')}
        />
      ),
      align: 'center',
      cell: ({ row }) => (
        <PlanConsistencyStatusBadge status={row.consistencyStatus} />
      ),
    },
    // [#7865] ワークフローID は**ステータスの直後 (左から 2 列目)** を既定位置と
    // する (2026-09-14 Des 判断)。 実測では WF ID 列を持つ 17 テーブルの位置が
    // 2/4/6/6/7/7/8/9/11/12/13/14/14/15 と全域に散っており、 守るべき共通規則が
    // 存在しなかったため既定を決めた。
    //
    // ステータスの隣に置くのは、 WF ID が**行の同一性** (どの案件か) を表すため。
    // 行の状態 (ステータス) と並べると「何が・どういう状態か」 が左端で読み切れる。
    //
    // 8080 (`design/plan/procurement.html`) は既にこの位置へ移動済み
    // (ebalance-prototype#346)。 列順の SoT がプロトタイプ側にあるのは
    // `conventions.md`「UIの詳細はプロトタイプに委譲」 による。
    {
      key: 'workflowId',
      header: t('page:procurementPlanList.table.workflowId'),
      align: 'left',
      // 字面は需要想定一覧 (`DemandForecastList/components/ForecastTable`) ・
      // 業務ワークフロー一覧 (`WorkflowCases/components/CaseTable`) と揃える
      // (2026-08-27 デザイナー指摘「同じワークフロー ID が画面によって別の
      // 字送りで出ている」)。 桁揃え (tabular-nums) と折り返し抑止は cell 側で
      // はなく列オプションに寄せる。
      nowrap: true,
      format: 'number',
      // AC-PPL-007a: 業務ワークフローに紐付く計画は human-readable ID
      // (workflowId) をリンクとして表示し、クリックで case UUID
      // (workflowCaseId) の遷移先 /workflow/cases/:caseId へ遷移する
      // (procurement-plan-list-workflow-id-link design Decision 8/9)。
      //
      // 3 分岐 (Decision 8):
      //   workflowId 非空 + workflowCaseId 非空 → リンク
      //   workflowId 非空 + workflowCaseId 空   → plain text (遷移キー欠落の
      //     防御分岐。通常データでは到達しない — design §States 参照)
      //   workflowId 空                          → 「—」
      cell: ({ row }) => {
        if (!row.workflowId) return '—'
        if (!row.workflowCaseId) return row.workflowId
        return (
          // DS Link を被せて hover 下線・focus ring を design `.ecl-text-link`
          // と揃える (ForecastTable.tsx の同型実装に合わせる)。
          <Link asChild>
            <RouterLink
              onClick={(e) => {
                e.stopPropagation()
              }}
              to={getWorkflowCasePath(row.workflowCaseId)}
            >
              {row.workflowId}
            </RouterLink>
          </Link>
        )
      },
    },
    {
      key: 'targetPeriod',
      header: t('page:procurementPlanList.table.targetPeriod'),
      align: 'left',
      // spec 列定義 (docs/specs/plan-submit/procurement-plan-list.md L152):
      // 対象期間列は日付のみ (yyyy/MM/dd)。時間帯 (0:00~24:00) と計画断面ラベル
      // (翌日(当日)/翌日/翌々日) は表示しない (issue #6364, PdM 承認 2026-08-25)。
      cell: ({ row }) =>
        row.targetPeriodStartDate !== undefined
          ? formatDateDisplay(fromProtoDate(row.targetPeriodStartDate))
          : '—',
    },
    {
      key: 'area',
      header: t('page:procurementPlanList.table.area'),
      align: 'center',
      cell: ({ row }) => convertAreaLabel(row.areaCode) || '—',
    },
    {
      key: 'bgCode',
      header: t('page:procurementPlanList.table.bgCode'),
      align: 'left',
      // AC-PPL-007c: 版数 (例「v2」) は表示しない。BG コード + BG 名称のみ表示する。
      //
      // **太字にしない** (2026-09-02 デザイナー判断)。 コードを label (md/600) に
      // していたのは、 1 列併記時代に**同じセルの中で**コードと名称の主従を付ける
      // ためだった (名称を薄字 lowEmphasis の 12px にすると読めない、 という
      // 2026-08-28 の判断で主従を色から太さへ移した)。 列を分けた本変更では
      // **列見出しが区別を担う**ため、 セル内で主従を付ける理由が消える。
      // 広域マスタ (`admin/occto.html` は元から 2 列) も値を太字にしていない。
      //
      // design 8080 (plan/procurement.html:206,874-877) はこの列を .cell-stack の
      // 縦 2 段にしているが、 その .cell-stack は「基準 UI = 需要想定一覧の
      // BG コード・名称列」を根拠にしており (components.css:352-)、 その需要想定一覧
      // 自体が #6699 で横並びに変わった。 よってこの列に限り 8080 が古い。
      cell: ({ row }) => (
        <Text as="span" textStyle="tableCell" fontFamily="mono">
          {row.bgCode}
        </Text>
      ),
    },
    {
      // 2026-09-01 デザイナー指摘「dev 全体で『BG コード・名称』 は 2 つのカラムに
      // 分けて表示」。 ⚠️ spec AC-DFL-006 [P0] / AC-WFW-001 と衝突する変更で、
      // AC の再判断は issue #6952 で PdM に確認中。 **解決前にマージしないこと。**
      // 詳細な経緯は `DemandForecastList/components/ForecastTable.tsx` の同名列の
      // コメントを参照 (両画面は「情報構造を揃える」 判断で対になっている)。
      key: 'bgName',
      header: t('page:procurementPlanList.table.bgName'),
      align: 'left',
      cell: ({ row }) =>
        row.bgName === undefined || row.bgName === '' ? (
          '—'
        ) : (
          <Text as="span" textStyle="tableCell" color="Text.highEmphasis">
            {row.bgName}
          </Text>
        ),
    },
    {
      key: 'lastUpdatedAt',
      ...sortableColumnOf(
        'lastUpdatedAt',
        t('page:procurementPlanList.table.lastUpdatedAt'),
      ),
      align: 'left',
      cell: ({ row }) => formatTimestamp(row.lastUpdatedAt),
    },
    {
      key: 'submittedAt',
      ...sortableColumnOf(
        'submittedAt',
        t('page:procurementPlanList.table.submittedAt'),
      ),
      align: 'left',
      cell: ({ row }) => formatTimestamp(row.submittedAt),
    },
  ]

  const detailPath = (id: string): string => {
    const base = routePaths.planProcurementDetail.path.replace(':id', id)
    return caseId !== undefined
      ? `${base}?${WORKFLOW_ID_PARAM}=${caseId}`
      : base
  }

  return (
    <MasterDataTable
      // 「ダウンロード」(icon + ラベル + caret) + 「詳細」が収まる幅。 旧 160px は「XML」単独ボタン用。
      actionColWidth="240px"
      columns={columns}
      emptyLabel={t('page:procurementPlanList.table.empty')}
      getRowKey={({ row }) => row.id}
      isLoading={isLoading}
      onRowClick={({ row }, e) => {
        openOrNavigate(navigate, detailPath(row.id), e)
      }}
      renderRowActions={({ row }) => {
        // AC-PPL-027: 作成中は理由をボタンの下に文で常に出し、 作成失敗はツールチップだけで示す
        // (downloadBlockReason)。 ツールチップは非活性の時に常に付ける (文の時は補助)。
        const downloadReason = canDownloadXml(row.status)
          ? undefined
          : downloadBlockReason(row.status)
        return (
          <VStack alignItems="flex-end" gap={1}>
            <HStack gap={ROW_ACTION_GAP}>
              <DownloadFormatMenuButton
                disabled={!canDownloadXml(row.status)}
                disabledReason={
                  downloadReason !== undefined
                    ? t(downloadReason.key)
                    : undefined
                }
                onDownload={(format) => void download(row.id, format)}
              />
              <TertiaryButton
                // **[DS差異]** 行アクションの文字は **md (14px) 維持**。 design
                // `css/components.css:1182-1189` の `.ecl-table td.cell-actions .btn` が
                // 「font-size は md (14px、 DS 既定) 維持。 height のみ 40 → 32 に縮め、
                //  padding も 12px に圧縮」 と明記している。 縮めるのは高さと padding だけで
                // 文字は縮めない (2026-09-03 デザイナー指摘)。
                //
                // `size="sm"` は**高さと padding のためだけ**に置いている。 recipe は
                // font-size まで sm に落とすので、 prop で md を明示して上書きする
                // (chakra v3 は非レイヤーの prop 宣言が `@layer recipes` に常に勝つ)。
                // prop を消すだけでは recipe の sm が残る (実測で確認)。
                // DS の size recipe に逆らう意図的な差異なので `[DS差異]` として記録する
                // (design/CLAUDE.md 5.)。 根拠は上記 design の行アクション規約。
                height="32px"
                fontSize="md"
                asChild
                paddingX={3}
                size="sm"
              >
                <RouterLink to={detailPath(row.id)}>
                  {t('page:procurementPlanList.table.actionDetail')}
                </RouterLink>
              </TertiaryButton>
            </HStack>
            {downloadReason?.display === 'text' && (
              <ActionReasonText textAlign="right">
                {t(downloadReason.key)}
              </ActionReasonText>
            )}
          </VStack>
        )
      }}
      rows={sortProcurementPlanRows(rows, sort)}
    />
  )
}
